import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Navigate } from 'react-router-dom';

export default function PasswordResetPage() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [session, setSession] = useState<any>(null);
    const [isValidToken, setIsValidToken] = useState(false);

    useEffect(() => {
        // Check if this is a valid password recovery session
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const type = hashParams.get('type');
            const recovery = hashParams.get('recovery');

            console.log('Password reset page - checking session:', {
                hasSession: !!session,
                type,
                recovery,
                hash: window.location.hash
            });

            // Check if this is a recovery session or if we have the recovery flag
            if ((type === 'recovery' || recovery === 'true') && session) {
                setSession(session);
                setIsValidToken(true);
            } else if (type !== 'recovery' && recovery !== 'true') {
                setError('Invalid password reset link. Please request a new password reset.');
            } else {
                setError('Password reset link has expired or is invalid. Please request a new one.');
            }
        };

        checkSession();
    }, []);

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValidToken || !session) {
            setError('Invalid or expired password reset session.');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;

            setSuccess('Password updated successfully! Redirecting to dashboard...');

            // Clear the URL hash and redirect after a short delay
            setTimeout(() => {
                window.location.href = '/';
            }, 2000);

        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isValidToken && !error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto mb-8">
                            <img
                                src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                                alt="Vault Contact Logo"
                                className="h-24 w-24 mx-auto object-contain animate-pulse"
                            />
                        </div>
                        <h2 className="text-5xl font-light text-white mb-3 tracking-wide">VAULT CONTACT</h2>
                        <div className="h-px w-32 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-4"></div>
                        <p className="text-gray-300 text-lg font-light mb-6">Validating Reset Link...</p>
                        <div className="mt-6 inline-flex items-center px-4 py-2 rounded-sm bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-3"></div>
                            <span className="text-gray-300 text-sm font-medium tracking-wide">AUTHENTICATING...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !isValidToken) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto mb-8">
                            <img
                                src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                                alt="Vault Contact Logo"
                                className="h-24 w-24 mx-auto object-contain"
                            />
                        </div>
                        <h2 className="text-5xl font-light text-white mb-3 tracking-wide">VAULT CONTACT</h2>
                        <div className="h-px w-32 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-4"></div>
                        <p className="text-gray-300 text-lg font-light mb-6">Private Contacts</p>
                    </div>

                    <div className="bg-black/40 backdrop-blur-xl rounded-none border border-gray-800/50 p-8 shadow-2xl">
                        <div className="mb-6 p-4 bg-red-900/30 border border-red-800/50 backdrop-blur-sm">
                            <p className="text-red-300 text-sm font-medium">{error}</p>
                        </div>

                        <button
                            onClick={() => window.location.href = '/'}
                            className="w-full px-4 py-4 bg-white text-black font-semibold hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-300 tracking-wide text-sm"
                        >
                            RETURN TO VAULT
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto mb-8">
                            <img
                                src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                                alt="Vault Contact Logo"
                                className="h-24 w-24 mx-auto object-contain"
                            />
                        </div>
                        <h2 className="text-5xl font-light text-white mb-3 tracking-wide">VAULT CONTACT</h2>
                        <div className="h-px w-32 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-4"></div>
                        <p className="text-gray-300 text-lg font-light mb-6">Access Restored</p>
                    </div>

                    <div className="bg-black/40 backdrop-blur-xl rounded-none border border-gray-800/50 p-8 shadow-2xl">
                        <div className="mb-6 p-4 bg-green-900/30 border border-green-800/50 backdrop-blur-sm">
                            <p className="text-green-300 text-sm font-medium">{success}</p>
                        </div>

                        <div className="text-center">
                            <div className="inline-flex items-center px-4 py-2 rounded-sm bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-3"></div>
                                <span className="text-gray-300 text-sm font-medium tracking-wide">REDIRECTING...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto mb-8">
                        <img
                            src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                            alt="Griswold Family Crest"
                            className="h-24 w-24 mx-auto object-contain"
                        />
                    </div>
                    <h2 className="text-5xl font-light text-white mb-3 tracking-wide">THE GRISWOLD VAULT</h2>
                    <div className="h-px w-32 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-4"></div>
                    <p className="text-gray-300 text-lg font-light mb-6">Reset Access Code</p>
                    <div className="mt-6 inline-flex items-center px-4 py-2 rounded-sm bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></div>
                        <span className="text-gray-300 text-sm font-medium tracking-wide">SECURE CONNECTION</span>
                    </div>
                </div>

                <form onSubmit={handleResetPassword} className="mt-8 space-y-6">
                    <div className="bg-black/40 backdrop-blur-xl rounded-none border border-gray-800/50 p-8 shadow-2xl">
                        {error && (
                            <div className="mb-6 p-4 bg-red-900/30 border border-red-800/50 backdrop-blur-sm">
                                <p className="text-red-300 text-sm font-medium">{error}</p>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="password" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                                    New Access Code
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-4 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium"
                                    placeholder="Enter your new password"
                                    required
                                    minLength={6}
                                />
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                                    Confirm Access Code
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full px-4 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium"
                                    placeholder="Confirm your new password"
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full mt-8 px-4 py-4 bg-white text-black font-semibold hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed tracking-wide text-sm"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-3"></div>
                                    UPDATING ACCESS...
                                </div>
                            ) : (
                                'UPDATE ACCESS CODE'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
